import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useNotify } from 'react-admin';
import MAutoComplete from '../../../components/controls/MAutoComplete';
import MDialog from '../../../components/controls/MDialog';
import MCheckbox from '../../../components/controls/MCheckbox';
import MNumber from '../../../components/controls/MNumber';

/**
 * Configuration object for history pre-filter settings
 */
interface HistoryPreFilterConfig {
    hasAvailableLots: boolean;
    excludedPurchasedSeats: boolean;
    excludedMarkets: string[];
    seatsRemainPercent: number;
}

/**
 * Filtering rule structure
 */
interface FilteringRule {
    variable: string;
    condition: string;
    value: any;
}

/**
 * Market choice object structure
 */
interface MarketChoice {
    name: string;
    id: string | number;
}

/**
 * Props for the HistoryPrefilterConfigDialog component
 */
interface HistoryPrefilterConfigDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Callback when dialog is closed */
    onClose: () => void;
    /** Array of history pre-filtering rules */
    historyPrefilteringRules: FilteringRule[];
    /** Callback when configuration is saved with new rules */
    onSave: (rules: FilteringRule[]) => void;
    /** Available market type choices */
    marketTypes?: any[];
}

// Default configuration values
const defaultHistoryFilterConfig: HistoryPreFilterConfig = {
    hasAvailableLots: true,
    excludedPurchasedSeats: true,
    excludedMarkets: ['exact', 'spektrix', 'tele', 'ticketforce'],
    seatsRemainPercent: 0
};

/**
 * HistoryPrefilterConfigDialog
 * Dialog component for configuring history pre-filter settings including
 * available lots, purchased seat exclusion, and market exclusion.
 * Follows the bidirectional mapping pattern (UI state <-> rule array).
 */
const HistoryPrefilterConfigDialog: React.FC<HistoryPrefilterConfigDialogProps> = ({
    open,
    onClose,
    historyPrefilteringRules,
    onSave,
    marketTypes = []
}) => {
    const notify = useNotify();
    const [historyPreFilterConfig, setHistoryPreFilterConfig] = useState<HistoryPreFilterConfig>(defaultHistoryFilterConfig);

    /**
     * Populate configuration from historyPrefilteringRules when dialog opens.
     * Maps rule array -> UI state (bidirectional mapping).
     */
    useEffect(() => {
        if (open && historyPrefilteringRules && historyPrefilteringRules.length > 0) {
            const currentConfig: HistoryPreFilterConfig = { ...defaultHistoryFilterConfig };

            // Extract hasAvailableLots rule
            const availableLotsRule = historyPrefilteringRules.find(rule => rule.variable === 'hasAvailableLots');
            if (availableLotsRule !== undefined) {
                currentConfig.hasAvailableLots = availableLotsRule.condition === 'equals' && availableLotsRule.value === true;
            }

            // Extract excludedPurchasedSeats rule
            const excludedPurchasedSeatsRule = historyPrefilteringRules.find(rule => rule.variable === 'excludedPurchasedSeats');
            if (excludedPurchasedSeatsRule !== undefined) {
                currentConfig.excludedPurchasedSeats = excludedPurchasedSeatsRule.condition === 'equals' && excludedPurchasedSeatsRule.value === true;
            }

            // Extract excludedMarkets rule
            const excludedMarketsRule = historyPrefilteringRules.find(rule => rule.variable === 'excludedMarkets' && rule.condition === 'not_in_list');
            if (excludedMarketsRule && Array.isArray(excludedMarketsRule.value)) {
                currentConfig.excludedMarkets = excludedMarketsRule.value;
            }

            // Extract seats remain percent rule
            const seatsRemainRule = historyPrefilteringRules.find(rule => rule.variable === 'seats_remain_percent');
            if (seatsRemainRule !== undefined) {
                currentConfig.seatsRemainPercent = seatsRemainRule.value;
            }

            setHistoryPreFilterConfig(currentConfig);
        } else if (open) {
            // Reset to default values when opening with no existing rules
            setHistoryPreFilterConfig(defaultHistoryFilterConfig);
        }
    }, [open, historyPrefilteringRules]);

    /**
     * Handle save configuration.
     * Maps UI state -> rule array (bidirectional mapping).
     */
    const handleSave = (): void => {
        const newRules: FilteringRule[] = [];

        // Add hasAvailableLots rule
        newRules.push({
            variable: 'hasAvailableLots',
            condition: 'equals',
            value: historyPreFilterConfig.hasAvailableLots
        });

        // Add excludedPurchasedSeats rule
        newRules.push({
            variable: 'excludedPurchasedSeats',
            condition: 'equals',
            value: historyPreFilterConfig.excludedPurchasedSeats
        });

        // Add excludedMarkets rule
        newRules.push({
            variable: 'excludedMarkets',
            condition: 'not_in_list',
            value: historyPreFilterConfig.excludedMarkets || []
        });

        // Add seats remain percent rule if specified
        if (historyPreFilterConfig.seatsRemainPercent !== null && historyPreFilterConfig.seatsRemainPercent !== undefined) {
            newRules.push({
                variable: 'seats_remain_percent',
                condition: 'less_than_or_equal',
                value: historyPreFilterConfig.seatsRemainPercent
            });
        }

        onSave(newRules);
        onClose();
        notify('History pre-filter configuration saved successfully', { type: 'success' });
    };

    /**
     * Handle cancel - just close dialog without saving
     */
    const handleCancel = (): void => {
        onClose();
    };

    // Build market choices from provided marketTypes or use default list
    const marketChoices: MarketChoice[] = marketTypes.length > 0
        ? marketTypes
        : ['exact', 'spektrix', 'tele', 'ticketforce'].map(m => ({ id: m, name: m }));

    return (
        <MDialog
            open={open}
            handleClose={handleCancel}
            title="History Pre-filter Configuration"
            name="History-pre-filter-config"
            maxWidth="md"
            action={
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                >
                    Save
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                    <MCheckbox
                        label="Has Available Lots"
                        defaultValue={historyPreFilterConfig.hasAvailableLots}
                        onChange={(checked: boolean) => setHistoryPreFilterConfig(prev => ({ ...prev, hasAvailableLots: checked }))}
                        key={`has-available-lots-${historyPreFilterConfig.hasAvailableLots}`}
                    />
                    <MCheckbox
                        label="Exclude Purchased Seats"
                        defaultValue={historyPreFilterConfig.excludedPurchasedSeats}
                        onChange={(checked: boolean) => setHistoryPreFilterConfig(prev => ({ ...prev, excludedPurchasedSeats: checked }))}
                        key={`excluded-purchased-seats-${historyPreFilterConfig.excludedPurchasedSeats}`}
                    />
                    <MNumber
                        label="% seats remain"
                        sx={{ width: '15%', m: 1 }}
                        value={historyPreFilterConfig.seatsRemainPercent}
                        onChange={(newValue: number) => setHistoryPreFilterConfig(prev => ({ ...prev, seatsRemainPercent: newValue }))}
                        decimal={2}
                        range={[-1, 100]}
                        isNegative={false}
                        size="small"
                        tooltip="Input 0 to disable this filter"
                    />
                </Box>
                <MAutoComplete
                    label="Excluded Markets"
                    choices={marketChoices}
                    onChange={(values: MarketChoice[] | string[]) => {
                        const marketNames: string[] = values
                            ? values.map((m: MarketChoice | string) => typeof m === 'string' ? m : m.name)
                            : [];
                        setHistoryPreFilterConfig(prev => ({ ...prev, excludedMarkets: marketNames }));
                    }}
                    value={historyPreFilterConfig.excludedMarkets.map(m => ({ name: m, id: m }))}
                    variant="standard"
                    isMulti={true}
                />
            </Box>
        </MDialog>
    );
};

export default HistoryPrefilterConfigDialog;
